import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronRight, RotateCcw, Calendar, Sparkles, Wallet } from "lucide-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import FloatingBookCTA from "@/components/site/FloatingBookCTA";
import LeadCaptureForm from "@/components/lead/LeadCaptureForm";
import { submitEuQuiz } from "@/lib/lead-api";
import usePageMeta from "@/lib/use-page-meta";

/* ------- EU readiness checklist definition ------- */
const QUESTIONS = [
  {
    id: "passport",
    title: "Who holds the EU, EEA or Swiss passport?",
    sub: "This shapes the whole route — including what a non-EU family member needs to do.",
    options: [
      { value: "me", label: "I do", score: 95 },
      { value: "partner", label: "My spouse or partner does", score: 80 },
      { value: "both", label: "We both do", score: 100 },
      { value: "none", label: "Neither of us — just exploring options", score: 15 },
    ],
    weight: 25,
  },
  {
    id: "timeline",
    title: "When would you like to be living in Spain?",
    sub: "Honestly — pick the option closest to your gut feeling.",
    options: [
      { value: "0-6", label: "Within 6 months", score: 100 },
      { value: "6-12", label: "6 to 12 months", score: 90 },
      { value: "12-24", label: "1 to 2 years", score: 60 },
      { value: "24+", label: "2+ years from now", score: 30 },
      { value: "unsure", label: "I'm not sure yet", score: 10 },
    ],
    weight: 25,
  },
  {
    id: "family",
    title: "Who's moving with you?",
    options: [
      { value: "solo", label: "Just me", score: 90 },
      { value: "couple-eu", label: "My partner and me — both EU", score: 90 },
      { value: "couple-mixed", label: "My partner and me — one of us is non-EU", score: 70 },
      { value: "family", label: "Family with children", score: 75 },
    ],
    weight: 10,
  },
  {
    id: "income",
    title: "How will you support yourselves in Spain?",
    sub: "This affects registration evidence, healthcare and whether tax advice matters.",
    options: [
      { value: "pension", label: "Pension / retirement income", score: 90 },
      { value: "remote", label: "Remote work or self-employment", score: 80 },
      { value: "savings", label: "Savings", score: 75 },
      { value: "work", label: "I'll find work in Spain", score: 65 },
      { value: "unsure", label: "Not sure yet", score: 40 },
    ],
    weight: 10,
  },
  {
    id: "healthcare",
    title: "Do you know your likely healthcare route?",
    sub: "Travel insurance is not the same as cover for living in Spain.",
    options: [
      { value: "s1", label: "Yes — S1 (state pension age)", score: 95 },
      { value: "private", label: "Yes — private cover", score: 85 },
      { value: "work", label: "Through work / self-employment", score: 85 },
      { value: "unsure", label: "No idea yet", score: 40 },
    ],
    weight: 10,
  },
  {
    id: "accommodation",
    title: "What's your accommodation plan?",
    sub: "Accommodation and local registration (padrón) are connected.",
    options: [
      { value: "own", label: "We already own a home in Spain", score: 95 },
      { value: "buying", label: "Buying before we move", score: 85 },
      { value: "longterm", label: "Long-term rental", score: 80 },
      { value: "shortterm", label: "Short-term first, then decide", score: 60 },
      { value: "unsure", label: "Not sure yet", score: 40 },
    ],
    weight: 10,
  },
  {
    id: "stuck",
    title: "Where do you feel least clear right now?",
    sub: "Pick the one that's bothering you most.",
    options: [
      { value: "paperwork", label: "Documents, translations and apostilles", score: 70 },
      { value: "order", label: "The order to do everything in", score: 75 },
      { value: "healthcare", label: "Healthcare", score: 65 },
      { value: "mixed", label: "A non-EU family member's requirements", score: 60 },
      { value: "researching", label: "Everything — just researching", score: 40 },
      { value: "ready", label: "I'm ready, just need a clear plan", score: 95 },
    ],
    weight: 10,
  },
];

function calcScore(answers) {
  let total = 0;
  let weightSum = 0;
  for (const q of QUESTIONS) {
    const a = answers[q.id];
    if (!a) continue;
    const opt = q.options.find((o) => o.value === a);
    if (opt) {
      total += (opt.score * q.weight) / 100;
      weightSum += q.weight;
    }
  }
  return Math.round((total / weightSum) * 100);
}

function tierFor(score) {
  if (score >= 80) return {
    key: "ready",
    label: "Ready to plan",
    headline: "You're ready to plan your move.",
    body: "Your passport situation, timeline and plans all stack up. The next step is a personal call with Carley to map your circumstances into a clear, properly sequenced plan.",
    accent: "var(--terracotta)",
  };
  if (score >= 60) return {
    key: "nearly",
    label: "Nearly there",
    headline: "You're nearly there.",
    body: "One or two pieces need tightening — likely healthcare, accommodation and registration, or a non-EU family member's paperwork. A short call with Carley will pinpoint exactly which gap to close first.",
    accent: "var(--terracotta)",
  };
  if (score >= 40) return {
    key: "building",
    label: "Building the foundations",
    headline: "You're building the foundations.",
    body: "You've got real intent but a few open questions. Read how the move actually works for EU passport holders on the EU Approved page — by the end you'll know exactly what to do next.",
    accent: "var(--gold)",
  };
  return {
    key: "exploring",
    label: "Still exploring",
    headline: "Still exploring — and that's fine.",
    body: "You're earlier in the journey. Read the EU Approved page for the complete picture of how the move works, so when your plans firm up you already know what's coming.",
    accent: "var(--gold)",
  };
}

/* ------- Component ------- */
export default function EuReadiness() {
  usePageMeta(
    "The EU Passport Readiness Checklist | Get to Spain",
    "Free 2-minute readiness check for EU, EEA and Swiss passport holders moving to Spain. Get a personalised score and a clear next step."
  );
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState("intro"); // intro | questions | form | result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const score = useMemo(() => calcScore(answers), [answers]);
  const tier = useMemo(() => tierFor(score), [score]);
  const progress = phase === "questions" ? ((step) / QUESTIONS.length) * 100 : phase === "form" ? 92 : 100;

  const pickAnswer = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
    setTimeout(() => {
      if (step < QUESTIONS.length - 1) setStep(step + 1);
      else setPhase("form");
    }, 250);
  };

  const handleSubmit = async (name, email, consent) => {
    const data = {
      name,
      email,
      answers,
      score,
      tier: tier.key,
      consent_marketing: consent,
      source: "/eu-readiness",
    };
    await submitEuQuiz(data);
    setResult({ name, email });
    setPhase("result");
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setPhase("intro");
    setResult(null);
  };

  const q = QUESTIONS[step];

  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <SiteNav />

      <section className="pt-28 pb-20 lg:pt-32 lg:pb-24 editorial relative overflow-hidden">
        <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 -left-32 w-[400px] h-[400px] rounded-full bg-[var(--gold)]/15 blur-3xl pointer-events-none" />

        <div className="relative max-w-3xl mx-auto px-6 lg:px-10">
          <Link to="/tools" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)]" data-testid="eu-quiz-back">
            <ArrowLeft className="w-4 h-4" /> Back to free tools
          </Link>

          {phase !== "intro" && (
            <div className="mt-8 h-1 rounded-full bg-[var(--ink)]/10 overflow-hidden">
              <div className="h-full bg-[var(--terracotta)] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          )}

          {/* INTRO */}
          {phase === "intro" && (
            <div className="mt-10 fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--cream-deep)] border border-[var(--terracotta)]/20 text-xs uppercase tracking-[0.2em] text-[var(--terracotta-deep)] font-medium">
                <Sparkles className="w-3.5 h-3.5" /> Free · 2 minutes · EU passport route
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--navy)] mt-6 leading-[1.04]" data-testid="eu-quiz-headline">
                The EU Passport <em className="text-[var(--terracotta)]">Readiness Checklist.</em>
              </h1>
              <p className="mt-6 text-lg text-[var(--ink-soft)] leading-relaxed">
                Your EU passport opens the door — but it doesn't tell you what paperwork to prepare,
                how healthcare works or what a non-EU family member needs. Answer 7 short questions
                and get a personalised readiness score with a clear next step.
              </p>
              <button
                onClick={() => setPhase("questions")}
                className="mt-10 pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
                data-testid="eu-quiz-start"
              >
                Start the checklist <ArrowRight className="w-5 h-5" />
              </button>
              <p className="mt-4 text-xs text-[var(--muted)]">No spam. Email only required at the end if you want your full report.</p>
            </div>
          )}

          {/* QUESTIONS */}
          {phase === "questions" && q && (
            <div className="mt-10 fade-up" key={step}>
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--terracotta)] font-medium">
                Question {step + 1} of {QUESTIONS.length}
              </div>
              <h2 className="font-serif text-3xl lg:text-4xl text-[var(--navy)] mt-4 leading-tight">{q.title}</h2>
              {q.sub && <p className="mt-3 text-[var(--ink-soft)]">{q.sub}</p>}

              <div className="mt-8 space-y-3" data-testid={`eu-quiz-options-${q.id}`}>
                {q.options.map((o) => {
                  const selected = answers[q.id] === o.value;
                  return (
                    <button
                      key={o.value}
                      onClick={() => pickAnswer(q.id, o.value)}
                      className={`w-full text-left flex items-center justify-between gap-4 px-5 py-4 rounded-2xl border transition-all duration-300 ${
                        selected
                          ? "bg-[var(--terracotta)] text-[var(--cream)] border-[var(--terracotta)]"
                          : "bg-white border-black/10 hover:border-[var(--terracotta)]/40 hover:bg-[var(--cream-deep)]/40"
                      }`}
                      data-testid={`eu-quiz-opt-${q.id}-${o.value}`}
                    >
                      <span className={`font-medium ${selected ? "text-[var(--cream)]" : "text-[var(--ink)]"}`}>{o.label}</span>
                      <ChevronRight className="w-4 h-4 opacity-60" />
                    </button>
                  );
                })}
              </div>

              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="mt-6 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)] inline-flex items-center gap-1"
                  data-testid="eu-quiz-prev"
                >
                  <ArrowLeft className="w-4 h-4" /> Previous question
                </button>
              )}
            </div>
          )}

          {/* EMAIL GATE */}
          {phase === "form" && (
            <div className="mt-10 fade-up">
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--terracotta)] font-medium">Final step</div>
              <h2 className="font-serif text-3xl lg:text-4xl text-[var(--navy)] mt-4 leading-tight">
                Where shall we send your <em>full report?</em>
              </h2>
              <p className="mt-3 text-[var(--ink-soft)]">
                You'll see your score on the next screen. Carley will follow up if your readiness suggests it's time for a call.
              </p>
              <div className="mt-8 rounded-3xl bg-white p-7 lg:p-8 shadow-xl border border-black/5">
                <LeadCaptureForm
                  onSubmit={handleSubmit}
                  ctaLabel="See my EU readiness score"
                  fbEvent="Lead"
                  fbEventParams={{ content_name: "EU Passport Readiness Checklist", source: "/eu-readiness" }}
                />
              </div>
            </div>
          )}

          {/* RESULT */}
          {phase === "result" && (
            <div className="mt-10 fade-up">
              <div className="rounded-[2rem] bg-[var(--navy)] text-[var(--cream)] p-10 lg:p-14 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-72 h-72 rounded-full pointer-events-none" style={{ background: `${tier.accent}25`, filter: "blur(80px)" }} />
                <div className="relative">
                  <div className="text-xs uppercase tracking-[0.3em] font-medium" style={{ color: tier.accent }}>Your EU move readiness</div>
                  <div className="mt-6 flex items-end gap-3">
                    <div className="font-serif text-7xl lg:text-8xl leading-none" data-testid="eu-quiz-score">{score}</div>
                    <div className="pb-2 text-2xl text-[var(--cream)]/70">/ 100</div>
                  </div>
                  <h2 className="mt-8 font-serif text-3xl lg:text-5xl leading-tight">{tier.headline}</h2>
                  <p className="mt-5 text-lg text-[var(--cream)]/85 leading-relaxed max-w-2xl">{tier.body}</p>

                  <div className="mt-10 flex flex-wrap gap-3">
                    <a
                      href="/eu-approved#book"
                      className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
                      data-testid="eu-quiz-cta-book"
                    >
                      <Calendar className="w-5 h-5" /> Book my call
                    </a>
                    <a
                      href="/eu-approved"
                      className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--cream)]/25 text-[var(--cream)] hover:bg-[var(--cream)] hover:text-[var(--navy)] font-medium"
                      data-testid="eu-quiz-cta-eupage"
                    >
                      See how EU Approved works <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Link to="/tools" className="rounded-2xl p-6 bg-white border border-black/5 hover:border-[var(--terracotta)]/30 transition-colors flex items-start gap-4" data-testid="eu-quiz-cross-tools">
                  <Wallet className="w-6 h-6 text-[var(--terracotta)] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-serif text-lg text-[var(--navy)]">Explore all the free tools</div>
                    <div className="text-sm text-[var(--ink-soft)] mt-1">Checklists, quizzes and planning helpers.</div>
                  </div>
                </Link>
                <a href="/eu-approved#faq" className="rounded-2xl p-6 bg-white border border-black/5 hover:border-[var(--terracotta)]/30 transition-colors flex items-start gap-4" data-testid="eu-quiz-cross-faq">
                  <Sparkles className="w-6 h-6 text-[var(--terracotta)] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-serif text-lg text-[var(--navy)]">Read the EU Approved FAQs</div>
                    <div className="text-sm text-[var(--ink-soft)] mt-1">Healthcare, mixed-nationality couples, timing and more.</div>
                  </div>
                </a>
              </div>

              <button
                onClick={restart}
                className="mt-8 inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)]"
                data-testid="eu-quiz-restart"
              >
                <RotateCcw className="w-4 h-4" /> Retake the checklist
              </button>
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
      <FloatingBookCTA href="/eu-approved#book" label="Book a Call" />
    </main>
  );
}
